/**
 * Gap composition (R18).
 *
 * A percentage is not an answer. "45% fit for Product Manager" reads as
 * encouragement, and the cohort found the flaw in it immediately: classic agile
 * PM work — ticket-moving, status documentation — is being eliminated, while
 * technically-deep PM work is not. A single number cannot tell those apart, so
 * a person reading 45% has no way to know which one they are being pointed at.
 *
 * Naming the missing 55% task by task fixes that without needing a better
 * model. Someone who reads "you would be arbitrating technical trade-offs and
 * owning roadmap prioritisation against engineering constraints" calibrates
 * themselves in about four seconds — far better than any fit score could, because
 * they know things about their own capability that no résumé encodes.
 *
 * So the fix for the false positive and the most-requested feature turn out to
 * be the same build.
 *
 * Everything here is set arithmetic over data already imported. No model call,
 * which means it works on the free tier where most people will meet it.
 */


/**
 * Do two work activities describe the same work?
 *
 * Exact string equality is the obvious test and it is wrong. O*NET phrases the
 * same underlying work differently across occupations — an IT project manager
 * "manages information technology projects or system activities" while an IS
 * manager "develops computer or information systems" — so set intersection over
 * the raw names reported that a project manager moving to systems manager would
 * find 0% of the job familiar and 100% new. That is not a hard truth about the
 * move; it is an artefact of vocabulary, and it is the kind of number that
 * destroys trust in every other number on the page.
 *
 * Dice coefficient over content words, with the generic verbs that appear in
 * hundreds of activities discounted, so "manage" alone never carries a match.
 */
const ACTIVITY_STOP = new Set([
  "or", "and", "the", "to", "of", "for", "with", "in", "on", "a", "an", "other",
  "others", "such", "as", "by", "from", "their", "its", "about",
]);

const GENERIC_VERB = new Set([
  "manage", "develop", "provide", "prepare", "conduct", "perform", "maintain",
  "coordinate", "direct", "monitor", "review", "evaluate", "determine",
  "establish", "implement", "identify", "assess", "communicate", "discuss",
]);

const activityTokens = (a) => {
  const toks = String(a || "").toLowerCase().replace(/[^a-z0-9\s]/g, " ").split(/\s+/)
    .filter((w) => w.length > 2 && !ACTIVITY_STOP.has(w));
  return new Set(toks);
};

/** Weighted Dice — generic verbs count for a fifth of a content word. */
function activitySimilarity(a, b) {
  const A = activityTokens(a), B = activityTokens(b);
  if (!A.size || !B.size) return 0;
  const w = (t) => (GENERIC_VERB.has(t) ? 0.2 : 1);
  let shared = 0, wa = 0, wb = 0;
  for (const t of A) { wa += w(t); if (B.has(t)) shared += w(t); }
  for (const t of B) wb += w(t);
  return (2 * shared) / (wa + wb || 1);
}

const SAME_WORK = 0.55;

/** Is this destination activity already something the person does? */
const alreadyDoing = (activity, owned, ownedList) =>
  owned.has(activity) || ownedList.some((o) => activitySimilarity(activity, o) >= SAME_WORK);

/**
 * What changes between where someone is and where they are going.
 *
 * @param {object} opts
 * @param {string} opts.fromCode          current occupation
 * @param {string} opts.toCode            destination occupation
 * @param {object} opts.dwaIndex          dwas.json — code -> [activity]
 * @param {Map}    opts.occByCode         code -> occupation record
 * @param {Array}  opts.skillDict         skills.json
 * @param {string[]} opts.confirmed       activities the person confirmed doing
 * @param {Set}    opts.evidencedSkillIds skills evidenced in their own history
 */
/**
 * @param emphasisSkillIds  Skills the DESTINATION requires that its anchor
 *   occupation does not rate highly. Emerging roles borrow an O*NET occupation
 *   for their activity data (there is no O*NET row for "Agent Operations
 *   Lead"), and without this the borrowed row is the whole analysis — so the
 *   emerging role silently becomes the ordinary occupation it borrowed, and
 *   everything that made it worth showing is discarded at the click.
 */
export function computeGap({
  fromCode, toCode, dwaIndex, occByCode, skillDict,
  confirmed = [], evidencedSkillIds = new Set(), emphasisSkillIds = [],
} = {}) {
  // A destination that IS the person's current occupation is not a move.
  //
  // This shipped: emerging roles carry a hardcoded anchor code, six of which
  // are among the most common resolutions in the cohort, and nothing checked
  // whether the anchor was the user's own occupation. An IT project manager
  // offered "Agent Operations Lead" — anchored to IT project manager — saw
  // "24/24 skills you already have, 0 parts of the work that would be new",
  // both content columns empty, and a terminal screen that told them they were
  // already finished. The most flattering possible output, produced by
  // comparing someone to themselves.
  //
  // Reframed rather than hidden: for an emerging role, the anchor matching your
  // own occupation is a real and interesting finding — the new work is a
  // redesign of the job you already hold, not a move out of it. The activity
  // half is meaningless here (it is a set compared with itself) and the caller
  // must not present it; the skills half still carries signal, because
  // `emphasisSkillIds` describes work the anchor row does not.
  const isRedesign = Boolean(fromCode && toCode && fromCode === toCode);
  const from = new Set(dwaIndex?.[fromCode] || []);
  const to = dwaIndex?.[toCode] || [];

  // Anything the person CONFIRMED counts as theirs even when the occupation we
  // matched them to does not list it. People do more than their job title says,
  // and telling someone they need to learn something they told us they already
  // do is the fastest way to lose them (ADR-033).
  const owned = new Set([...from, ...confirmed]);

  const ownedList = [...owned];
  const carried = to.filter((a) => alreadyDoing(a, owned, ownedList));
  const carriedSet = new Set(carried);
  const newActivities = to.filter((a) => !carriedSet.has(a));

  // Skills: what the destination rates highly that this person has not evidenced.
  const target = occByCode?.get(toCode);
  const byId = new Map((skillDict || []).map((s) => [s.id, s]));
  const IMPORTANT = 3.2;

  // The anchor's own highly-rated skills, plus anything the emerging role
  // demands that the anchor does not. An emphasis skill is pinned at the
  // importance threshold when the anchor rates it lower — it is required by the
  // destination whatever the borrowed row happens to say.
  const rated = new Map(
    Object.entries(target?.skills || {}).filter(([, v]) => v >= IMPORTANT)
  );
  for (const id of emphasisSkillIds) {
    if (!rated.has(id)) rated.set(id, IMPORTANT);
  }
  const emphasis = new Set(emphasisSkillIds);
  const wanted = [...rated.entries()].sort((a, b) => b[1] - a[1]);

  const missingSkills = [];
  const reusedSkills = [];
  for (const [id, importance] of wanted) {
    const s = byId.get(id);
    if (!s || s.category === "ai-signals-human") continue;
    const row = {
      id, name: s.name, category: s.category,
      importance: Math.round(importance * 10) / 10,
      // Flagged so the UI can say which skills are the emerging part of the
      // role rather than the borrowed occupation's ordinary requirements.
      defining: emphasis.has(id),
    };
    (evidencedSkillIds.has(id) ? reusedSkills : missingSkills).push(row);
  }

  // The headline percentage comes from SKILLS, not activities — and getting this
  // wrong once was instructive. Deriving it from activity overlap reported that
  // an IT project manager moving to IS manager would find 0% of the job familiar
  // and 100% new, which is absurd on its face and would have discredited every
  // other number on the screen.
  //
  // The cause is not fixable by better matching: O*NET's activity lists are a
  // SAMPLE of each occupation's work, 18 items here against 24 there, and two
  // samples of overlapping jobs can share almost no entries. Fuzzy matching only
  // trades a false 0% for false claims that someone already does work they do
  // not — a worse error, because it flatters.
  //
  // Skill ratings are dense, comparable, and rated on the same scale for every
  // occupation. So: skills carry the number, activities carry the narrative.
  // "You already have 7 of the 10 skills this role leans on" is both truer and
  // more useful than any task-count ratio, and the activity list underneath is
  // where the real answer to "what would I actually be doing" lives.
  const ratedSkills = missingSkills.length + reusedSkills.length;
  return {
    fromCode,
    toCode,
    isRedesign,
    targetTitle: target?.title || null,
    // Meaningless when the destination is the current occupation — a set
    // compared with itself. Emptied rather than left for the UI to misread.
    newActivities: isRedesign ? [] : newActivities,
    carried: isRedesign ? [] : carried,
    carryPercent: ratedSkills ? Math.round((reusedSkills.length / ratedSkills) * 100) : null,
    skillsCarried: reusedSkills.length,
    skillsNeeded: ratedSkills,
    missingSkills,
    reusedSkills,
    // Inferred vectors are honest but weaker evidence, and the UI has to say so.
    skillsInferred: Boolean(target?.skillsInferred),
  };
}

/**
 * Match content to a gap (R19, R20).
 *
 * Every recommendation carries the reason it was made. A list of courses with
 * no stated connection to the person's own gap is advice, not a product — and
 * it is indistinguishable from the generic listicles this is meant to replace.
 *
 * Editions teach; modules let you demonstrate. Both are matched on the SAME
 * missing-skill set, so the two columns answer one question rather than two.
 */
export function matchContent(content, gap, { persona = null, limit = 5 } = {}) {
  const missing = new Map((gap?.missingSkills || []).map((s) => [s.id, s]));
  if (!missing.size) return { editions: [], modules: [], uncovered: [] };

  const score = (tags) => {
    const hits = (tags || []).filter((t) => missing.has(t.id));
    return { hits, n: hits.length };
  };

  const rank = (items, field) =>
    items
      .map((item) => {
        const { hits, n } = score(item[field]);
        return { ...item, closes: hits, matchCount: n };
      })
      .filter((item) => item.matchCount > 0)
      .filter((item) => !persona || !item.persona?.length || item.persona.includes(persona))
      // Most gap-skills closed first; ties broken by fewest unrelated tags, so a
      // focused piece beats a scattergun one that happens to mention everything.
      .sort((a, b) => b.matchCount - a.matchCount ||
                      (a[field]?.length || 0) - (b[field]?.length || 0))
      .slice(0, limit);

  const editions = rank(content?.editions || [], "teaches");
  const modules = rank(content?.modules || [], "builds");

  // Honesty about coverage. A gap skill nothing addresses should be visible, not
  // quietly dropped — for the reader it is the part of the plan that is missing,
  // and for the editorial calendar it is the next thing to write.
  const covered = new Set([
    ...editions.flatMap((e) => e.closes.map((s) => s.id)),
    ...modules.flatMap((m) => m.closes.map((s) => s.id)),
  ]);
  const uncovered = [...missing.values()].filter((s) => !covered.has(s.id));

  return { editions, modules, uncovered };
}
